import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';


function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

function PortfolioChart({
  loading = false,
  points = [],
  reportCurrency = 'USD',
  selectedRange = '180d',
  onChangeRange = () => {},
  portfolioName = 'Portfolio',
  benchmarkPoints = [],
  transactions = [],
  benchmarkTicker = '',
  benchmarkLoading = false,
  benchmarkError = false,
  walletOn = true,
  groupedHoldings = [],
  tickerColorMap = null,
  totalLineColor = null,
  investedLineColor = null,
  benchmarkLineColor = null,
  allRangeLabel = 'ALL',
}) {
  const isMobile = useIsMobile();
  const [hoverIndex, setHoverIndex] = useState(null);
  const [seriesMode, setSeriesMode] = useState('total');
  const [showInvested, setShowInvested] = useState(true);
  const [showBenchmark, setShowBenchmark] = useState(true);
  const [showBuyMarkers, setShowBuyMarkers] = useState(false);
  const [showSellMarkers, setShowSellMarkers] = useState(false);
  const [selectedBuyTickers, setSelectedBuyTickers] = useState([]);
  const [showRangeDropdown, setShowRangeDropdown] = useState(false);
  const [showVisibleDropdown, setShowVisibleDropdown] = useState(false);

  // Zoom / pan state
  const [viewStartIdx, setViewStartIdx] = useState(0);
  const [viewEndIdx, setViewEndIdx] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const svgRef = useRef(null);
  const dragRef = useRef(null);
  const isDraggingRef = useRef(false);
  const wheelStateRef = useRef({});
  const rangeDropdownRef = useRef(null);
  const visibleDropdownRef = useRef(null);

  React.useEffect(() => {
    if (!walletOn && seriesMode === 'cash') {
      setSeriesMode('total');
    }
  }, [walletOn, seriesMode]);

  // Reset zoom/pan when range changes
  useEffect(() => {
    setViewStartIdx(0);
    setViewEndIdx(null);
    setHoverIndex(null);
  }, [selectedRange]);

  // Close dropdowns on outside click
  useEffect(() => {
    if (!showRangeDropdown && !showVisibleDropdown) return;
    const handler = (e) => {
      if (rangeDropdownRef.current && !rangeDropdownRef.current.contains(e.target)) {
        setShowRangeDropdown(false);
      }
      if (visibleDropdownRef.current && !visibleDropdownRef.current.contains(e.target)) {
        setShowVisibleDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showRangeDropdown, showVisibleDropdown]);

  const chartData = useMemo(() => {
    const safePoints = Array.isArray(points) ? points : [];
    const mapped = safePoints
      .map((item) => {
        const rawDate =
          item?.date || item?.Date || item?.timestamp || item?.Timestamp || '';
        const parsedDate = rawDate ? new Date(rawDate) : null;

        return {
          rawDate,
          parsedDate,
          totalValueBase: Number(item?.totalValueBase ?? item?.TotalValueBase ?? 0),
          cashValueBase: Number(item?.cashValueBase ?? item?.CashValueBase ?? 0),
          holdingsValueBase: Number(
            item?.holdingsValueBase ?? item?.HoldingsValueBase ?? 0
          ),
          netContributionsBase: Math.max(
            0,
            Number(item?.netContributionsBase ?? item?.NetContributionsBase ?? 0)
          ),
          performancePnlBase: Number(
            item?.performancePnlBase ?? item?.PerformancePnlBase ?? 0
          ),
        };
      })
      .filter(
        (item) =>
          item.rawDate &&
          item.parsedDate &&
          !Number.isNaN(item.parsedDate.getTime())
      )
      .sort((a, b) => a.parsedDate - b.parsedDate);

    if (mapped.length === 0) {
      // No real data yet — generate synthetic flat-zero points so the chart renders
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return Array.from({ length: 7 }, (_, i) => {
        const d = new Date(today);
        d.setDate(d.getDate() - (6 - i));
        const iso = d.toISOString().split('T')[0];
        return {
          rawDate: iso,
          parsedDate: d,
          totalValueBase: 0,
          cashValueBase: 0,
          holdingsValueBase: 0,
          netContributionsBase: 0,
          performancePnlBase: 0,
        };
      });
    }
    if (mapped.length === 1) {
      return mapped;
    }

    const cleaned = [mapped[0]];

    for (let i = 1; i < mapped.length; i += 1) {
      const current = mapped[i];
      const prev = cleaned[cleaned.length - 1];

      const currentValue = Number(current.totalValueBase || 0);
      const prevValue = Number(prev.totalValueBase || 0);

      const suspiciousDrop =
        prevValue > 0 &&
        currentValue === 0 &&
        current.netContributionsBase === prev.netContributionsBase &&
        current.cashValueBase === prev.cashValueBase &&
        current.holdingsValueBase === 0;

      if (suspiciousDrop) {
        cleaned.push({
          ...current,
          totalValueBase: prev.totalValueBase,
          holdingsValueBase: prev.holdingsValueBase,
          performancePnlBase:
            Number(prev.totalValueBase || 0) - Number(current.netContributionsBase || 0),
        });
        continue;
      }

      cleaned.push(current);
    }

    return cleaned;
  }, [points]);

  // Effective view window
  const effEnd = viewEndIdx !== null ? Math.min(viewEndIdx, chartData.length - 1) : chartData.length - 1;
  const effStart = Math.min(viewStartIdx, Math.max(0, effEnd));

  // The slice of data actually displayed
  const displayData = useMemo(() => {
    if (!chartData.length) return chartData;
    return chartData.slice(effStart, effEnd + 1);
  }, [chartData, effStart, effEnd]);

  const activeValueKey =
    seriesMode === 'cash'
      ? 'cashValueBase'
      : seriesMode === 'holdings'
      ? 'holdingsValueBase'
      : 'totalValueBase';

  const activeSeriesLabel =
    seriesMode === 'cash'
      ? 'Cash'
      : seriesMode === 'holdings'
      ? 'Holdings'
      : 'Total';

  const activeSeriesColor =
    seriesMode === 'cash'
      ? '#0ea5e9'
      : seriesMode === 'holdings'
      ? '#22c55e'
      : (totalLineColor || '#8b7cff');

  const investedColor = investedLineColor || '#f59e0b';
  const benchmarkColor = benchmarkLineColor || '#06b6d4';

  // Map benchmark prices to chart data dates.
  const benchmarkRawPrices = useMemo(() => {
    const safeBenchmark = Array.isArray(benchmarkPoints) ? benchmarkPoints : [];
    if (!safeBenchmark.length || !chartData.length) return [];

    const isIntraday = selectedRange === '1d' || selectedRange === '7d';

    const bMap = new Map();
    safeBenchmark.forEach((item) => {
      const raw = item?.date || item?.Date || '';
      const price = Number(item?.price || item?.Price || 0);
      if (!raw || price <= 0) return;
      if (isIntraday) {
        if (!bMap.has(raw)) bMap.set(raw, price);
      } else {
        const dp = raw.split('T')[0];
        if (!bMap.has(dp)) bMap.set(dp, price);
      }
    });
    if (!bMap.size) return [];

    const lookupNearest = (s) => {
      if (isIntraday) {
        if (bMap.has(s)) return bMap.get(s);
        const baseTime = new Date(s).getTime();
        if (Number.isNaN(baseTime)) return null;
        let bestKey = null, bestDiff = Infinity;
        for (const [key] of bMap) {
          const diff = Math.abs(new Date(key).getTime() - baseTime);
          if (diff < bestDiff && diff <= 7_200_000) { bestDiff = diff; bestKey = key; }
        }
        return bestKey ? bMap.get(bestKey) : null;
      } else {
        const key = s.split('T')[0];
        if (bMap.has(key)) return bMap.get(key);
        const base = new Date(key);
        if (Number.isNaN(base.getTime())) return null;
        for (let off = 1; off <= 7; off++) {
          for (const sign of [-1, 1]) {
            const dt = new Date(base);
            dt.setDate(dt.getDate() + sign * off);
            const p = bMap.get(dt.toISOString().split('T')[0]);
            if (p !== undefined) return p;
          }
        }
        return null;
      }
    };

    return chartData.map((item) => lookupNearest(item.rawDate));
  }, [benchmarkPoints, chartData, selectedRange]);

  const benchmarkDisplayValues = useMemo(() => {
    if (!benchmarkRawPrices.length || !chartData.length) return [];

    let baseSPYPrice = null;
    for (let i = 0; i < chartData.length; i++) {
      const invested = Number(chartData[i]?.netContributionsBase || 0);
      const spyPrice = benchmarkRawPrices[i];
      if (invested > 0 && spyPrice !== null && spyPrice !== undefined && spyPrice > 0) {
        baseSPYPrice = spyPrice;
        break;
      }
    }

    if (!baseSPYPrice) {
      for (let i = 0; i < benchmarkRawPrices.length; i++) {
        const p = benchmarkRawPrices[i];
        if (p !== null && p !== undefined && p > 0) {
          baseSPYPrice = p;
          break;
        }
      }
    }

    if (!baseSPYPrice) return [];

    return chartData.map((item, i) => {
      const invested = Number(item?.netContributionsBase || 0);
      if (invested <= 0) return null;
      const currentSPY = benchmarkRawPrices[i];
      if (currentSPY === null || currentSPY === undefined || currentSPY <= 0) return null;
      return invested * (currentSPY / baseSPYPrice);
    });
  }, [benchmarkRawPrices, chartData]);

  // Slice benchmark values to display window
  const displayBenchmarkValues = useMemo(
    () => benchmarkDisplayValues.slice(effStart, effEnd + 1),
    [benchmarkDisplayValues, effStart, effEnd]
  );

  // Buy markers — full list from all chartData
  const allBuyTickers = useMemo(() => {
    const buys = (Array.isArray(transactions) ? transactions : []).filter(tx => tx.transactionType === 0);
    return [...new Set(buys.map(tx => (tx.instrument?.ticker || tx.ticker || '').toUpperCase()).filter(Boolean))].sort();
  }, [transactions]);

  const buyMarkersFull = useMemo(() => {
    if (!chartData.length) return [];
    const buys = (Array.isArray(transactions) ? transactions : []).filter(tx => tx.transactionType === 0);
    const grouped = new Map();
    buys.forEach(tx => {
      const txDate = new Date((tx.occurredAt || '').split('T')[0]);
      if (Number.isNaN(txDate.getTime())) return;
      const ticker = (tx.instrument?.ticker || tx.ticker || '').toUpperCase();
      if (selectedBuyTickers.length > 0 && !selectedBuyTickers.includes(ticker)) return;
      let bestIdx = -1, bestDiff = Infinity;
      chartData.forEach((p, i) => {
        if (!p.parsedDate) return;
        const diff = Math.abs(p.parsedDate.getTime() - txDate.getTime());
        if (diff < bestDiff && diff <= 7 * 86_400_000) { bestDiff = diff; bestIdx = i; }
      });
      if (bestIdx === -1) return;
      const key = `${bestIdx}-${ticker}`;
      if (!grouped.has(key)) grouped.set(key, { index: bestIdx, ticker, count: 0, qty: 0 });
      const entry = grouped.get(key);
      entry.count += 1;
      entry.qty += Number(tx.quantity || 0);
    });
    return Array.from(grouped.values());
  }, [chartData, transactions, selectedBuyTickers]);

  const buyMarkers = useMemo(() =>
    buyMarkersFull.filter(m => m.index >= effStart && m.index <= effEnd).map(m => ({ ...m, index: m.index - effStart })),
  [buyMarkersFull, effStart, effEnd]);

  // Sell markers
  const sellMarkersFull = useMemo(() => {
    if (!chartData.length) return [];
    const sells = (Array.isArray(transactions) ? transactions : []).filter(tx => tx.transactionType === 1);
    const grouped = new Map();
    sells.forEach(tx => {
      const txDate = new Date((tx.occurredAt || '').split('T')[0]);
      if (Number.isNaN(txDate.getTime())) return;
      const ticker = (tx.instrument?.ticker || tx.ticker || '').toUpperCase();
      let bestIdx = -1, bestDiff = Infinity;
      chartData.forEach((p, i) => {
        if (!p.parsedDate) return;
        const diff = Math.abs(p.parsedDate.getTime() - txDate.getTime());
        if (diff < bestDiff && diff <= 7 * 86_400_000) { bestDiff = diff; bestIdx = i; }
      });
      if (bestIdx === -1) return;
      const key = `${bestIdx}-${ticker}`;
      if (!grouped.has(key)) grouped.set(key, { index: bestIdx, ticker, count: 0, qty: 0 });
      const entry = grouped.get(key);
      entry.count += 1;
      entry.qty += Number(tx.quantity || 0);
    });
    return Array.from(grouped.values());
  }, [chartData, transactions]);

  const sellMarkers = useMemo(() =>
    sellMarkersFull.filter(m => m.index >= effStart && m.index <= effEnd).map(m => ({ ...m, index: m.index - effStart })),
  [sellMarkersFull, effStart, effEnd]);

  const width = 1000;
  const height = 360;
  const paddingLeft = 0;
  const paddingRight = 0;
  const paddingTop = 14;
  const paddingBottom = 20;

  const values = displayData.flatMap((p, i) => {
    const base = [Number(p[activeValueKey] || 0), Number(p.netContributionsBase || 0)];
    if (showBenchmark && displayBenchmarkValues.length > i && displayBenchmarkValues[i] !== null) {
      base.push(displayBenchmarkValues[i]);
    }
    return base;
  });

  const minValueRaw = values.length ? Math.min(...values) : 0;
  const maxValueRaw = values.length ? Math.max(...values) : 0;

  const dataRange = Math.abs(maxValueRaw - minValueRaw);
  const valuePadding = Math.max(dataRange * 0.08, Math.max(Math.abs(maxValueRaw) * 0.02, 1));

  // When portfolio is empty (all values 0), keep original ±1 range (line in center)
  const allZero = minValueRaw === 0 && maxValueRaw === 0;

  const normalizedMin =
    minValueRaw === maxValueRaw ? minValueRaw - 1 : minValueRaw - valuePadding;
  const normalizedMax =
    minValueRaw === maxValueRaw ? maxValueRaw + 1 : maxValueRaw + valuePadding;

  const getX = (index) => {
    if (displayData.length <= 1) return 0;
    return (index / (displayData.length - 1)) * width;
  };

  const getY = (value) => {
    const usableHeight = height - paddingTop - paddingBottom;
    const ratio = (value - normalizedMin) / (normalizedMax - normalizedMin || 1);
    return height - paddingBottom - ratio * usableHeight;
  };

  const linePath = displayData
    .map((point, index) => {
      const x = getX(index);
      const y = getY(Number(point[activeValueKey] || 0));
      return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
    })
    .join(' ');

  const investedPath = displayData
    .map((point, index) => {
      const x = getX(index);
      const y = getY(Number(point.netContributionsBase || 0));
      return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
    })
    .join(' ');

  const benchmarkPath = (() => {
    const parts = [];
    displayBenchmarkValues.forEach((val, i) => {
      if (val !== null) {
        parts.push(`${parts.length === 0 ? 'M' : 'L'} ${getX(i)} ${getY(val)}`);
      }
    });
    return parts.join(' ');
  })();

  const lastBenchmarkValue = displayBenchmarkValues.length
    ? [...displayBenchmarkValues].reverse().find((v) => v !== null) ?? null
    : null;

  const areaPath = (displayData.length && !allZero)
    ? `${linePath} L ${width} ${height} L 0 ${height} Z`
    : '';

  // For performance calculation, use full chartData (not zoomed)
  const firstPoint = chartData[0];
  const lastPoint = chartData[chartData.length - 1];

  const lastSeriesValue = Number(lastPoint?.[activeValueKey] || 0);
  const firstSeriesValue = Number(firstPoint?.[activeValueKey] || 0);
  const lastNetContributions = Number(lastPoint?.netContributionsBase || 0);

  const isShortRange = selectedRange === '1d' || selectedRange === '7d';

  let performanceChange;
  let performancePercent;

  if (isShortRange && firstSeriesValue > 0) {
    const periodChange = lastSeriesValue - firstSeriesValue;
    performanceChange = periodChange;
    performancePercent = (periodChange / firstSeriesValue) * 100;
  } else if (lastNetContributions > 0) {
    const pnl = lastSeriesValue - lastNetContributions;
    performanceChange = pnl;
    performancePercent = (pnl / lastNetContributions) * 100;
  } else {
    const periodChange = lastSeriesValue - firstSeriesValue;
    performanceChange = periodChange;
    performancePercent = firstSeriesValue !== 0 ? (periodChange / Math.abs(firstSeriesValue)) * 100 : 0;
  }

  const firstBenchmarkValue = benchmarkDisplayValues.length
    ? benchmarkDisplayValues.find((v) => v !== null) ?? null
    : null;
  const benchmarkPeriodPct =
    lastBenchmarkValue !== null
      ? isShortRange && firstBenchmarkValue !== null && firstBenchmarkValue > 0
        ? ((lastBenchmarkValue - firstBenchmarkValue) / firstBenchmarkValue) * 100
        : lastNetContributions > 0
        ? ((lastBenchmarkValue - lastNetContributions) / lastNetContributions) * 100
        : null
      : null;

  const plColor =
    performanceChange > 0
      ? 'var(--success)'
      : performanceChange < 0
      ? 'var(--danger)'
      : 'var(--text-primary)';

  const rangeOptions = [
    { value: '1d', label: '1D' },
    { value: '7d', label: '7D' },
    { value: '30d', label: '1M' },
    { value: '90d', label: '3M' },
    { value: '180d', label: '6M' },
    { value: 'ytd', label: 'YTD' },
    { value: '1y', label: '1Y' },
    { value: '3y', label: '3Y' },
    { value: '5y', label: '5Y' },
    { value: 'all', label: allRangeLabel },
  ];

  const seriesOptions = [
    { value: 'total', label: 'Total' },
    { value: 'holdings', label: 'Holdings' },
    { value: 'cash', label: 'Cash' },
  ];

  const hoverPoint =
    hoverIndex != null && hoverIndex >= 0 && hoverIndex < displayData.length
      ? displayData[hoverIndex]
      : null;

  const tooltipX = hoverPoint ? getX(hoverIndex) : 0;
  const tooltipY = hoverPoint ? getY(Number(hoverPoint[activeValueKey] || 0)) : 0;

  const yTicks = (minValueRaw === 0 && maxValueRaw === 0) ? [0] : buildYTicks(normalizedMin, normalizedMax);
  const xTickIndexes = buildXAxisTickIndexes(displayData.length);

  // ── Zoom / Pan event handlers ─────────────────────────────────────────────
  wheelStateRef.current = { chartData, effStart, effEnd, selectedRange, onChangeRange };

  const handleWheelStable = useCallback((e) => {
    e.preventDefault();
    const { chartData: cd, effStart: es, effEnd: ee } = wheelStateRef.current;
    if (!cd.length) return;

    const svgEl = svgRef.current;
    if (!svgEl) return;
    const rect = svgEl.getBoundingClientRect();
    const svgW = rect.width || 1;
    const chartAreaLeft = (paddingLeft / width) * svgW;
    const chartAreaRight = ((width - paddingRight) / width) * svgW;
    const mouseInChart = e.clientX - rect.left;
    const chartFrac = Math.max(0, Math.min(1, (mouseInChart - chartAreaLeft) / (chartAreaRight - chartAreaLeft)));

    const currentSpan = ee - es + 1;
    const isZoomOut = e.deltaY > 0;
    const factor = isZoomOut ? 1.18 : (1 / 1.18);
    const newSpan = Math.max(10, Math.min(cd.length, Math.round(currentSpan * factor)));

    const anchorIdx = es + chartFrac * currentSpan;
    let newStart = Math.round(anchorIdx - chartFrac * newSpan);
    let newEnd = newStart + newSpan - 1;

    if (newStart < 0) { newEnd = Math.min(cd.length - 1, newEnd - newStart); newStart = 0; }
    if (newEnd >= cd.length) { newStart = Math.max(0, newStart - (newEnd - cd.length + 1)); newEnd = cd.length - 1; }
    newStart = Math.max(0, newStart);
    newEnd = Math.min(cd.length - 1, newEnd);

    setViewStartIdx(newStart);
    setViewEndIdx(newEnd);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const svgCallbackRef = useCallback((el) => {
    if (svgRef.current) {
      svgRef.current.removeEventListener('wheel', handleWheelStable);
    }
    svgRef.current = el;
    if (el) {
      el.addEventListener('wheel', handleWheelStable, { passive: false });
    }
  }, [handleWheelStable]);

  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    isDraggingRef.current = true;
    setIsDragging(true);
    dragRef.current = { startX: e.clientX, startViewStart: effStart, startViewEnd: effEnd };
  }, [effStart, effEnd]);

  const handleMouseMoveDrag = useCallback((e) => {
    if (!isDraggingRef.current || !dragRef.current || !chartData.length) return;
    const rect = svgRef.current?.getBoundingClientRect();
    const svgW = rect?.width || 1;
    const chartAreaW = ((width - paddingLeft - paddingRight) / width) * svgW;
    const dx = dragRef.current.startX - e.clientX;
    const span = dragRef.current.startViewEnd - dragRef.current.startViewStart + 1;
    const deltaIdx = Math.round((dx / chartAreaW) * span);

    let newStart = dragRef.current.startViewStart + deltaIdx;
    let newEnd = dragRef.current.startViewEnd + deltaIdx;

    if (newStart < 0) { newEnd -= newStart; newStart = 0; }
    if (newEnd >= chartData.length) { newStart -= (newEnd - chartData.length + 1); newEnd = chartData.length - 1; }
    newStart = Math.max(0, newStart);
    newEnd = Math.min(chartData.length - 1, newEnd);

    setViewStartIdx(newStart);
    setViewEndIdx(newEnd);
  }, [chartData.length]);

  const handleMouseUpGlobal = useCallback(() => {
    isDraggingRef.current = false;
    setIsDragging(false);
    dragRef.current = null;
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMoveDrag);
    window.addEventListener('mouseup', handleMouseUpGlobal);
    return () => {
      window.removeEventListener('mousemove', handleMouseMoveDrag);
      window.removeEventListener('mouseup', handleMouseUpGlobal);
    };
  }, [handleMouseMoveDrag, handleMouseUpGlobal]);

  return (
    <section style={cardStyle}>
      {/* Mobile header */}
      {isMobile ? (
        <div style={mobileHeaderStyle}>
          <h3 style={mobileTitleStyle}>{portfolioName}</h3>
          <div style={mobileMetricsStyle}>
            <strong style={{ ...mobilePlValueStyle, color: plColor }}>
              {performancePercent >= 0 ? '+' : ''}{performancePercent.toFixed(2)}%
            </strong>
            <span style={{ ...mobilePlSubStyle, color: plColor }}>
              {performanceChange >= 0 ? '+' : ''}{formatCurrency(performanceChange, reportCurrency)}
            </span>
            {lastPoint && (
              <>
                <span style={mobileValueLabelStyle}>Latest Total</span>
                <strong style={mobileValueStyle}>{formatCurrency(lastPoint[activeValueKey], reportCurrency)}</strong>
              </>
            )}
          </div>
        </div>
      ) : (
        <div style={headerStyle}>
          <div>
            <h3 style={titleStyle}>{portfolioName}</h3>
          </div>

          <div style={headerRightStyle}>
            <div style={plBoxStyle}>
              <span style={valueLabelStyle}>Performance for selected range</span>
              <strong style={{ ...plValueStyle, color: plColor }}>
                {performancePercent >= 0 ? '+' : ''}
                {performancePercent.toFixed(2)}%
              </strong>
              <span style={{ ...plSubValueStyle, color: plColor }}>
                {performanceChange >= 0 ? '+' : ''}
                {formatCurrency(performanceChange, reportCurrency)}
              </span>
            </div>

            {lastPoint ? (
              <div style={valueBoxStyle}>
                <span style={valueLabelStyle}>Latest {activeSeriesLabel}</span>
                <strong style={valueValueStyle}>
                  {formatCurrency(lastPoint[activeValueKey], reportCurrency)}
                </strong>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Mobile controls row */}
      {isMobile && (
        <div style={mobileControlsRowStyle}>
          {/* Visible button (left) */}
          <div ref={visibleDropdownRef} style={{ position: 'relative' }}>
            <button type="button" onClick={() => setShowVisibleDropdown(v => !v)} style={mobileDropdownBtnStyle}>
              <span>Visible</span>
              <span style={{ fontSize: '10px' }}>▾</span>
            </button>
            {showVisibleDropdown && (
              <div style={mobileDropdownPanelStyle}>
                {/* series mode */}
                {walletOn && seriesOptions.map(opt => (
                  <button key={opt.value} type="button"
                    onClick={() => { setSeriesMode(opt.value); }}
                    style={{ ...dropdownItemStyle, ...(seriesMode === opt.value ? dropdownItemActiveStyle : {}) }}>
                    {opt.label}
                  </button>
                ))}
                {walletOn && <div style={dropdownDividerStyle} />}
                {/* invested toggle */}
                <button type="button" onClick={() => setShowInvested(v => !v)} style={dropdownItemStyle}>
                  <span style={{ ...legendLineStyle, borderColor: investedColor, marginRight: 6 }} />
                  Invested {showInvested ? '✓' : ''}
                </button>
                {/* benchmark toggle */}
                {benchmarkTicker && (
                  <button type="button" onClick={() => setShowBenchmark(v => !v)} style={dropdownItemStyle}>
                    <span style={{ ...legendLineStyle, borderColor: benchmarkColor, marginRight: 6 }} />
                    {benchmarkTicker} {showBenchmark ? '✓' : ''}
                  </button>
                )}
                <div style={dropdownDividerStyle} />
                {/* buy markers */}
                <button type="button" onClick={() => setShowBuyMarkers(v => !v)} style={dropdownItemStyle}>
                  Buy markers {showBuyMarkers ? '✓' : ''}
                </button>
                {/* sell markers */}
                <button type="button" onClick={() => setShowSellMarkers(v => !v)} style={dropdownItemStyle}>
                  Sell markers {showSellMarkers ? '✓' : ''}
                </button>
                {showBuyMarkers && allBuyTickers.length > 1 && (
                  <>
                    <div style={{ padding: '4px 12px 2px', fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 600 }}>
                      Filter assets:
                    </div>
                    <button type="button"
                      onClick={() => setSelectedBuyTickers([])}
                      style={{ ...dropdownItemStyle, ...(selectedBuyTickers.length === 0 ? dropdownItemActiveStyle : {}) }}>
                      All assets
                    </button>
                    {allBuyTickers.map(t => (
                      <button key={t} type="button"
                        onClick={() => setSelectedBuyTickers(prev =>
                          prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]
                        )}
                        style={{ ...dropdownItemStyle, ...(selectedBuyTickers.includes(t) ? dropdownItemActiveStyle : {}) }}>
                        {t}
                      </button>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Range button (right) */}
          <div ref={rangeDropdownRef} style={{ position: 'relative' }}>
            <button type="button" onClick={() => setShowRangeDropdown(v => !v)} style={mobileDropdownBtnStyle}>
              <span>{rangeOptions.find(r => r.value === selectedRange)?.label || selectedRange}</span>
              <span style={{ fontSize: '10px' }}>▾</span>
            </button>
            {showRangeDropdown && (
              <div style={{ ...mobileDropdownPanelStyle, right: 0, left: 'auto' }}>
                {rangeOptions.map(item => (
                  <button key={item.value} type="button"
                    onClick={() => { onChangeRange(item.value); setShowRangeDropdown(false); }}
                    style={{ ...dropdownItemStyle, ...(selectedRange === item.value ? dropdownItemActiveStyle : {}) }}>
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Desktop toolbar */}
      {!isMobile && (
        <div style={toolbarStyle}>
          {walletOn ? (
            <div style={seriesBarStyle}>
              {seriesOptions.map((item) => {
                const active = seriesMode === item.value;

                return (
                  <button
                    key={item.value}
                    type="button"
                    onClick={() => setSeriesMode(item.value)}
                    style={{
                      ...seriesButtonStyle,
                      ...(active ? activeSeriesButtonStyle : null),
                    }}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>
          ) : null}

          <div style={rangeBarStyle}>
            {rangeOptions.map((item) => {
              const active = selectedRange === item.value;

              return (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => onChangeRange(item.value)}
                  style={{
                    ...rangeButtonStyle,
                    ...(active ? activeRangeButtonStyle : null),
                  }}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {loading ? (
        <p style={emptyTextStyle}>Loading chart...</p>
      ) : (
        <>
          {/* Desktop legend */}
          {!isMobile && (
            <div style={legendStyle}>
              <div style={legendItemStyle}>
                <span style={{ ...legendSwatchStyle, background: activeSeriesColor }} />
                <span>{activeSeriesLabel}</span>
              </div>

              <button
                type="button"
                onClick={() => setShowInvested((v) => !v)}
                style={{
                  ...legendToggleStyle,
                  opacity: showInvested ? 1 : 0.4,
                  textDecoration: showInvested ? 'none' : 'line-through',
                }}
                title={showInvested ? 'Hide Invested' : 'Show Invested'}
              >
                <span style={{ ...legendLineStyle, borderColor: investedColor }} />
                <span>Invested</span>
                {lastPoint ? (
                  <span style={{ color: investedColor, fontWeight: 800, fontSize: '13px' }}>
                    {formatCurrency(lastPoint.netContributionsBase, reportCurrency)}
                  </span>
                ) : null}
              </button>

              {benchmarkTicker ? (
                <button
                  type="button"
                  onClick={() => setShowBenchmark((v) => !v)}
                  style={{
                    ...legendToggleStyle,
                    opacity: showBenchmark ? (benchmarkPath ? 1 : 0.5) : 0.4,
                    textDecoration: showBenchmark ? 'none' : 'line-through',
                  }}
                  title={
                    benchmarkError
                      ? `${benchmarkTicker}: failed to load data`
                      : benchmarkLoading
                      ? `${benchmarkTicker}: loading...`
                      : showBenchmark
                      ? `Hide ${benchmarkTicker}`
                      : `Show ${benchmarkTicker}`
                  }
                >
                  <span style={{ ...legendLineStyle, borderColor: benchmarkError ? '#ef4444' : benchmarkColor }} />
                  <span style={benchmarkError ? { color: '#ef4444' } : undefined}>{benchmarkTicker}</span>
                  {benchmarkLoading && !benchmarkPath ? (
                    <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>načítání…</span>
                  ) : benchmarkError ? (
                    <span style={{ color: '#ef4444', fontSize: '12px' }}>chyba dat</span>
                  ) : lastBenchmarkValue !== null ? (
                    <span style={{ color: benchmarkColor, fontWeight: 800, fontSize: '13px' }}>
                      {formatCurrency(lastBenchmarkValue, reportCurrency)}
                      {benchmarkPeriodPct !== null
                        ? ` (${benchmarkPeriodPct >= 0 ? '+' : ''}${benchmarkPeriodPct.toFixed(2)}%)`
                        : ''}
                    </span>
                  ) : null}
                </button>
              ) : null}

              <button type="button" onClick={() => setShowBuyMarkers(v => !v)} style={{ ...legendToggleStyle, opacity: showBuyMarkers ? 1 : 0.4 }}>
                <span style={{ width: '2px', height: '14px', borderLeft: '2px dashed #8b7cff', display: 'inline-block', flexShrink: 0, marginRight: 4 }} />
                <span>Buys</span>
              </button>

              <button type="button" onClick={() => setShowSellMarkers(v => !v)} style={{ ...legendToggleStyle, opacity: showSellMarkers ? 1 : 0.4 }}>
                <span style={{ width: '2px', height: '14px', borderLeft: '2px solid #f87171', display: 'inline-block', flexShrink: 0, marginRight: 4 }} />
                <span>Sells</span>
              </button>
            </div>
          )}

          <div style={chartWrapperStyle}>
            {/* Y-axis labels — overlaid on right */}
            {yTicks.map((tick, i) => (
              <div key={`yl-${i}`} style={{
                position: 'absolute',
                right: '8px',
                top: `${getY(tick)}px`,
                transform: 'translateY(-50%)',
                fontSize: '11px',
                color: 'var(--text-secondary)',
                fontWeight: 600,
                pointerEvents: 'none',
                lineHeight: 1,
                zIndex: 2,
              }}>
                {formatAxisNumberShort(tick)}
              </div>
            ))}

            {/* X-axis date labels — overlaid at bottom */}
            {xTickIndexes.map((idx, i) => {
              const isFirst = i === 0;
              const isLast = i === xTickIndexes.length - 1;
              const pct = displayData.length <= 1 ? 0 : (idx / (displayData.length - 1)) * 100;
              return (
                <div key={`xl-${idx}`} style={{
                  position: 'absolute',
                  bottom: '6px',
                  fontSize: '11px',
                  color: 'var(--text-secondary)',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  pointerEvents: 'none',
                  zIndex: 2,
                  ...(isFirst
                    ? { left: '8px' }
                    : isLast
                    ? { right: '8px' }
                    : { left: `${pct}%`, transform: 'translateX(-50%)' }),
                }}>
                  {formatXAxisLabel(displayData[idx]?.parsedDate, selectedRange)}
                </div>
              );
            })}

            <svg
              ref={svgCallbackRef}
              viewBox={`0 0 ${width} ${height}`}
              style={{ ...svgStyle, cursor: isDragging ? 'grabbing' : 'grab' }}
              preserveAspectRatio="none"
              onMouseLeave={() => { if (!isDraggingRef.current) setHoverIndex(null); }}
              onMouseDown={handleMouseDown}
            >
              <defs>
                <linearGradient id="portfolioAreaGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={activeSeriesColor} stopOpacity="0.35" />
                  <stop offset="100%" stopColor={activeSeriesColor} stopOpacity="0.05" />
                </linearGradient>
              </defs>

              {yTicks.map((tick, index) => (
                <line
                  key={index}
                  x1={0}
                  y1={getY(tick)}
                  x2={width}
                  y2={getY(tick)}
                  style={gridStyle}
                />
              ))}

              {areaPath ? <path d={areaPath} fill="url(#portfolioAreaGradient)" /> : null}

              {showInvested && investedPath ? (
                <path
                  d={investedPath}
                  style={{
                    fill: 'none',
                    stroke: investedColor,
                    strokeWidth: 2,
                    strokeDasharray: '8 6',
                    strokeLinecap: 'round',
                    strokeLinejoin: 'round',
                    opacity: 0.95,
                  }}
                />
              ) : null}

              {showBenchmark && benchmarkPath ? (
                <path
                  d={benchmarkPath}
                  style={{
                    fill: 'none',
                    stroke: benchmarkColor,
                    strokeWidth: 2,
                    strokeDasharray: '8 6',
                    strokeLinecap: 'round',
                    strokeLinejoin: 'round',
                    opacity: 0.9,
                  }}
                />
              ) : null}

              {linePath ? (
                <path
                  d={linePath}
                  style={{
                    ...lineStyle,
                    stroke: activeSeriesColor,
                  }}
                />
              ) : null}

              {/* Buy markers — grouped by date position, stacked labels, per-ticker color */}
              {showBuyMarkers && (() => {
                const byIdx = new Map();
                buyMarkers.forEach(m => {
                  if (!byIdx.has(m.index)) byIdx.set(m.index, []);
                  byIdx.get(m.index).push(m);
                });
                return [...byIdx.entries()].flatMap(([idx, markers]) => {
                  const x = getX(idx);
                  return markers.map((marker, mi) => {
                    const color = (tickerColorMap && tickerColorMap.get(marker.ticker)) || '#8b7cff';
                    const shortTicker = marker.ticker
                      ? (marker.ticker.length <= 5 ? marker.ticker : marker.ticker.slice(0, 4) + '…')
                      : '';
                    return (
                      <g key={`buy-${idx}-${mi}`}>
                        <line x1={x} y1={paddingTop} x2={x} y2={height - paddingBottom}
                              stroke={color} strokeWidth="1.5" strokeDasharray="5 4" opacity="0.5" />
                        {shortTicker && (
                          <text x={x + 3} y={paddingTop + 14 + mi * 12}
                                style={{ fontSize: '9px', fill: color, fontWeight: 700, opacity: 0.9 }}>
                            {shortTicker}
                          </text>
                        )}
                      </g>
                    );
                  });
                });
              })()}

              {/* Sell markers — solid line in red */}
              {showSellMarkers && (() => {
                const byIdx = new Map();
                sellMarkers.forEach(m => {
                  if (!byIdx.has(m.index)) byIdx.set(m.index, []);
                  byIdx.get(m.index).push(m);
                });
                return [...byIdx.entries()].flatMap(([idx, markers]) => {
                  const x = getX(idx);
                  return markers.map((marker, mi) => {
                    const shortTicker = marker.ticker
                      ? (marker.ticker.length <= 5 ? marker.ticker : marker.ticker.slice(0, 4) + '…')
                      : '';
                    return (
                      <g key={`sell-${idx}-${mi}`}>
                        <line x1={x} y1={paddingTop} x2={x} y2={height - paddingBottom}
                              stroke="#f87171" strokeWidth="1.5" opacity="0.7" />
                        {shortTicker && (
                          <text x={x + 3} y={paddingTop + 14 + mi * 12}
                                style={{ fontSize: '9px', fill: '#f87171', fontWeight: 700, opacity: 0.9 }}>
                            {shortTicker}
                          </text>
                        )}
                      </g>
                    );
                  });
                });
              })()}

              {hoverPoint ? (
                <>
                  <line
                    x1={tooltipX}
                    y1={paddingTop}
                    x2={tooltipX}
                    y2={height - paddingBottom}
                    style={{
                      ...hoverLineStyle,
                      stroke: activeSeriesColor,
                    }}
                  />
                  <circle
                    cx={tooltipX}
                    cy={tooltipY}
                    r="6"
                    style={{
                      ...hoverPointOuterStyle,
                      stroke: activeSeriesColor,
                    }}
                  />
                  <circle
                    cx={tooltipX}
                    cy={tooltipY}
                    r="3.5"
                    style={{
                      ...hoverPointInnerStyle,
                      fill: activeSeriesColor,
                    }}
                  />
                </>
              ) : null}

              {displayData.map((point, index) => {
                const x = getX(index);
                const leftX = index === 0 ? x : (getX(index - 1) + x) / 2;
                const rightX =
                  index === displayData.length - 1
                    ? width - paddingRight
                    : (x + getX(index + 1)) / 2;

                return (
                  <rect
                    key={point.rawDate + '-' + index}
                    x={leftX}
                    y={paddingTop}
                    width={Math.max(rightX - leftX, 8)}
                    height={height - paddingTop - paddingBottom}
                    fill="transparent"
                    onMouseEnter={() => { if (!isDraggingRef.current) setHoverIndex(index); }}
                    onMouseMove={() => { if (!isDraggingRef.current) setHoverIndex(index); }}
                  />
                );
              })}
            </svg>

            {hoverPoint ? (
              <div
                style={{
                  ...tooltipStyle,
                  left: `${(tooltipX / width) * 100}%`,
                  top: `${Math.min(Math.max((tooltipY / height) * 100, 14), 72)}%`,
                  transform: (() => {
                    const pct = (tooltipX / width) * 100;
                    const tx = pct < 25 ? '0%' : pct > 70 ? '-100%' : '-50%';
                    return `translate(${tx}, -110%)`;
                  })(),
                }}
              >
                <div style={tooltipDateStyle}>
                  {formatDateLabel(hoverPoint.parsedDate, selectedRange, true)}
                </div>
                <div style={tooltipValueStyle}>
                  {activeSeriesLabel}: {formatCurrency(hoverPoint[activeValueKey], reportCurrency)}
                </div>
                <div style={tooltipSubValueStyle}>
                  Total: {formatCurrency(hoverPoint.totalValueBase, reportCurrency)}
                </div>
                <div style={tooltipSubValueStyle}>
                  Holdings: {formatCurrency(hoverPoint.holdingsValueBase, reportCurrency)}
                </div>
                <div style={tooltipSubValueStyle}>
                  Cash: {formatCurrency(hoverPoint.cashValueBase, reportCurrency)}
                </div>
                <div style={tooltipSubValueStyle}>
                  Invested: {formatCurrency(hoverPoint.netContributionsBase, reportCurrency)}
                </div>
                {benchmarkTicker && displayBenchmarkValues[hoverIndex] != null ? (() => {
                  const bv = displayBenchmarkValues[hoverIndex];
                  const hoverInvested = Number(hoverPoint?.netContributionsBase || 0);
                  const bPct = isShortRange && firstBenchmarkValue !== null && firstBenchmarkValue > 0
                    ? ((bv - firstBenchmarkValue) / firstBenchmarkValue) * 100
                    : hoverInvested > 0
                    ? ((bv - hoverInvested) / hoverInvested) * 100
                    : null;
                  return (
                    <div style={{ ...tooltipSubValueStyle, color: benchmarkColor }}>
                      {benchmarkTicker}: {formatCurrency(bv, reportCurrency)}
                      {bPct !== null ? ` (${bPct >= 0 ? '+' : ''}${bPct.toFixed(2)}%)` : ''}
                    </div>
                  );
                })() : null}
                <div style={tooltipSubValueStyle}>
                  Performance: {formatCurrency(hoverPoint.performancePnlBase, reportCurrency)}
                </div>
              </div>
            ) : null}
          </div>

        </>
      )}
    </section>
  );
}

function buildYTicks(min, max, targetCount = 12) {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [0];
  if (min === max) return [min];
  const range = max - min;
  const roughStep = range / (targetCount - 1);
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const norm = roughStep / magnitude;
  let niceStep;
  if (norm <= 1.5) niceStep = 1;
  else if (norm <= 3) niceStep = 2;
  else if (norm <= 4) niceStep = 2.5;
  else if (norm <= 7.5) niceStep = 5;
  else niceStep = 10;
  niceStep *= magnitude;
  const firstTick = Math.ceil(min / niceStep) * niceStep;
  const ticks = [];
  for (let t = firstTick; t <= max + niceStep * 0.01; t += niceStep) {
    ticks.push(Math.abs(Math.round(t * 1e9) / 1e9) < 1e-9 ? 0 : Math.round(t * 1e9) / 1e9);
    if (ticks.length >= 14) break;
  }
  return ticks;
}

function buildXAxisTickIndexes(length) {
  if (length <= 1) return [0];
  return [0, length - 1];
}

function formatAxisNumberShort(value) {
  const numberValue = Number(value || 0);
  const abs = Math.abs(numberValue);

  let compactValue = numberValue;
  let suffix = '';

  if (abs >= 1_000_000_000) {
    compactValue = numberValue / 1_000_000_000;
    suffix = 'B';
  } else if (abs >= 1_000_000) {
    compactValue = numberValue / 1_000_000;
    suffix = 'M';
  } else if (abs >= 1_000) {
    compactValue = numberValue / 1_000;
    suffix = 'k';
  }

  const formatted = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: abs >= 1000 ? 1 : 0,
  }).format(compactValue);

  return `${formatted}${suffix}`;
}

function formatXAxisLabel(date, selectedRange) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '';
  }

  if (selectedRange === '1d') {
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  if (selectedRange === '7d') {
    return new Intl.DateTimeFormat('en-US', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
    }).format(date);
  }

  if (selectedRange === '30d') {
    return new Intl.DateTimeFormat('en-US', {
      day: '2-digit',
      month: '2-digit',
    }).format(date);
  }

  if (selectedRange === '90d' || selectedRange === '180d' || selectedRange === 'ytd' || selectedRange === '1y') {
    return new Intl.DateTimeFormat('en-US', {
      day: '2-digit',
      month: '2-digit',
    }).format(date);
  }

  if (selectedRange === '3y' || selectedRange === '5y' || selectedRange === 'all') {
    return new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      year: '2-digit',
    }).format(date);
  }

  return new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  }).format(date);
}

function formatCurrency(value, currency) {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 2,
    }).format(Number(value || 0));
  } catch {
    return Number(value || 0).toFixed(2) + ' ' + (currency || '');
  }
}

function formatDateLabel(date, selectedRange, detailed = false) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '-';
  }

  if (selectedRange === '1d') {
    return new Intl.DateTimeFormat(
      'en-US',
      detailed
        ? {
            day: '2-digit',
            month: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          }
        : {
            hour: '2-digit',
            minute: '2-digit',
          }
    ).format(date);
  }

  if (selectedRange === '7d') {
    return new Intl.DateTimeFormat(
      'en-US',
      detailed
        ? {
            day: '2-digit',
            month: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          }
        : {
            day: '2-digit',
            month: '2-digit',
            hour: '2-digit',
          }
    ).format(date);
  }

  if (selectedRange === '30d') {
    return new Intl.DateTimeFormat(
      'en-US',
      detailed
        ? {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          }
        : {
            day: '2-digit',
            month: '2-digit',
          }
    ).format(date);
  }

  return new Intl.DateTimeFormat(
    'en-US',
    detailed
      ? {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }
      : {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }
  ).format(date);
}

const cardStyle = {
  background: 'var(--surface-strong)',
  borderRadius: '18px',
  padding: '20px 24px 0',
  marginBottom: '24px',
  boxShadow: 'var(--shadow)',
  border: '1px solid var(--border)',
  minWidth: 0,
  width: '100%',
  boxSizing: 'border-box',
  overflow: 'hidden',
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: '16px',
  flexWrap: 'wrap',
  marginBottom: '18px',
};

const headerRightStyle = {
  display: 'flex',
  gap: '18px',
  alignItems: 'stretch',
  flexWrap: 'wrap',
};

const titleStyle = {
  marginTop: 0,
  marginBottom: '8px',
  color: 'var(--text-primary)',
};

const valueBoxStyle = {
  display: 'grid',
  gap: '6px',
};

const plBoxStyle = {
  display: 'grid',
  gap: '6px',
};

const valueLabelStyle = {
  color: 'var(--text-secondary)',
  fontSize: '13px',
  fontWeight: 600,
  textAlign: 'right',
};

const valueValueStyle = {
  color: 'var(--text-primary)',
  fontSize: '22px',
};

const plValueStyle = {
  fontSize: '24px',
  fontWeight: 800,
  textAlign: 'right',
};

const plSubValueStyle = {
  fontSize: '14px',
  fontWeight: 700,
  textAlign: 'right',
};

const toolbarStyle = {
  display: 'grid',
  gap: '10px',
  marginBottom: '16px',
};

const seriesBarStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
};

const seriesButtonStyle = {
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text-primary)',
  borderRadius: '999px',
  padding: '8px 12px',
  fontSize: '13px',
  fontWeight: 700,
  cursor: 'pointer',
};

const activeSeriesButtonStyle = {
  background: 'var(--primary)',
  color: '#ffffff',
  border: '1px solid var(--primary)',
};

const rangeBarStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
};

const rangeButtonStyle = {
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text-primary)',
  borderRadius: '999px',
  padding: '8px 12px',
  fontSize: '13px',
  fontWeight: 700,
  cursor: 'pointer',
};

const activeRangeButtonStyle = {
  background: 'var(--primary)',
  color: '#ffffff',
  border: '1px solid var(--primary)',
};

const legendStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '16px',
  marginBottom: '12px',
};

const legendItemStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  color: 'var(--text-secondary)',
  fontSize: '13px',
  fontWeight: 600,
};

const legendSwatchStyle = {
  width: '14px',
  height: '14px',
  borderRadius: '999px',
  display: 'inline-block',
};

const legendLineStyle = {
  width: '20px',
  height: '0',
  borderTopWidth: '2px',
  borderTopStyle: 'dashed',
  display: 'inline-block',
};

const chartWrapperStyle = {
  marginLeft: '-24px',
  marginRight: '-24px',
  height: '360px',
  position: 'relative',
};

const svgStyle = {
  width: '100%',
  height: '100%',
  display: 'block',
};

const gridStyle = {
  stroke: 'var(--border)',
  strokeWidth: 1,
  strokeDasharray: '4 4',
  opacity: 0.5,
};

const legendToggleStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  color: 'var(--text-secondary)',
  fontSize: '13px',
  fontWeight: 600,
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  transition: 'opacity 0.15s',
};

const lineStyle = {
  fill: 'none',
  strokeWidth: 3,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.08))',
};

const hoverLineStyle = {
  strokeWidth: 1.5,
  strokeDasharray: '5 5',
  opacity: 0.7,
};

const hoverPointOuterStyle = {
  fill: '#ffffff',
  strokeWidth: 3,
};

const hoverPointInnerStyle = {};

const tooltipStyle = {
  position: 'absolute',
  transform: 'translate(-50%, -110%)',
  pointerEvents: 'none',
  background: 'rgba(15, 23, 42, 0.96)',
  color: '#ffffff',
  padding: '10px 12px',
  borderRadius: '12px',
  minWidth: '190px',
  boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
  border: '1px solid rgba(255,255,255,0.08)',
};

const tooltipDateStyle = {
  fontSize: '12px',
  opacity: 0.85,
  marginBottom: '8px',
};

const tooltipValueStyle = {
  fontSize: '14px',
  fontWeight: 800,
  marginBottom: '4px',
};

const tooltipSubValueStyle = {
  fontSize: '12px',
  opacity: 0.9,
};

const emptyTextStyle = {
  margin: 0,
  color: 'var(--text-secondary)',
};

// Mobile styles
const mobileHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: '12px',
  marginBottom: '14px',
};

const mobileTitleStyle = {
  margin: 0, color: 'var(--text-primary)', fontSize: '18px', fontWeight: 800,
};

const mobileMetricsStyle = {
  display: 'grid',
  gap: '2px',
  textAlign: 'right',
};

const mobilePlValueStyle = {
  fontSize: '18px', fontWeight: 800,
};

const mobilePlSubStyle = {
  fontSize: '12px', fontWeight: 700,
};

const mobileValueLabelStyle = {
  fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 600,
};

const mobileValueStyle = {
  fontSize: '16px', fontWeight: 800, color: 'var(--text-primary)',
};

const mobileControlsRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '12px',
};

const mobileDropdownBtnStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text-primary)',
  borderRadius: '999px',
  padding: '8px 14px',
  fontSize: '13px',
  fontWeight: 700,
  cursor: 'pointer',
};

const mobileDropdownPanelStyle = {
  position: 'absolute',
  top: 'calc(100% + 6px)',
  left: 0,
  background: 'var(--surface-strong)',
  border: '1px solid var(--border)',
  borderRadius: '14px',
  boxShadow: 'var(--shadow)',
  zIndex: 30,
  minWidth: '160px',
  padding: '6px',
  display: 'grid',
  gap: '2px',
};

const dropdownItemStyle = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  padding: '9px 12px',
  borderRadius: '9px',
  border: 'none',
  background: 'transparent',
  color: 'var(--text-primary)',
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: '13px',
  textAlign: 'left',
  gap: '6px',
};

const dropdownItemActiveStyle = {
  background: 'var(--primary)',
  color: '#ffffff',
};

const dropdownDividerStyle = {
  height: '1px',
  background: 'var(--border)',
  margin: '3px 0',
};

export default PortfolioChart;
